import { DatePicker, Form, Select } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { Dayjs } from 'dayjs';
import { useGetDropdownClosingList } from 'features/reports/use-cases/get-dropdownlist';
import { FormSectionsSeparator } from 'features/users/components/modals/form-sections-separator';
import { useEffect } from 'react';

const { RangePicker } = DatePicker;

export const ClosingReportInfoFormItems = ({
  handleDate,
  form,
}: {
  handleDate: (dates: [Dayjs | null, Dayjs | null] | null) => void;
  form: any;
}) => {
  const modelType = useWatch('modelType', form);
  // const companyId = useWatch('company_ids', form);
  const facilityId = useWatch('facility_ids', form);
  const fieldId = useWatch('field_id', form);

  // Fetching Company options

  const { data: companyOptions, isFetching: companyLoading } =
    useGetDropdownClosingList({ flag: 'COMPANY_DROPDOWN' }, { enabled: true });

  // Fetching Facility options
  const { data: facilityOptions, isFetching: facilityLoading } =
    useGetDropdownClosingList({ flag: modelType }, { enabled: !!modelType });

  // Fetching Field options
  const { data: fieldOptions, isFetching: fieldLoading } =
    useGetDropdownClosingList(
      { flag: modelType, facility_cascade_id: facilityId },
      { enabled: !!facilityId },
    );

  // Fetching Sport options
  const { data: sportOptions, isFetching: sportLoading } =
    useGetDropdownClosingList(
      {
        flag: modelType,
        facility_cascade_id: facilityId,
        field_cascade_id: fieldId,
      },
      { enabled: !!fieldId },
    );

  // Reset all fields when modelType changes
  useEffect(() => {
    if (!modelType) {
      form.setFieldsValue({
        company_ids: undefined,
        facility_ids: undefined,
        field_id: undefined,
        sports_id: undefined,
        date: undefined,
      });
    } else {
      form.setFieldsValue({
        company_ids: undefined,
        facility_ids: undefined,
        field_id: undefined,
        sports_id: undefined,
        date: undefined,
      });
    }
  }, [modelType, form]);

  // Reset field_id and sports_id when facility_ids changes
  useEffect(() => {
    form.setFieldsValue({
      field_id: undefined,
      sports_id: undefined,
    });
  }, [facilityId, form]);

  // Reset sports_id when field_id changes
  useEffect(() => {
    form.setFieldsValue({
      sports_id: undefined,
    });
  }, [fieldId, form]);

  const getSelectOptions = (data: any) =>
    data?.items?.map((item: any) => ({
      label: item?.Text,
      value: item?.Value,
    })) || [];

  return (
    <FormSectionsSeparator title=''>
      <Form.Item
        label='Model Type'
        name='modelType'
        rules={[{ required: true, message: 'Please Select a Model Type' }]}
      >
        <Select
          allowClear
          placeholder='Select Model Type'
          options={[
            { value: 'AllBooking', label: 'AllBooking' },
            { value: 'LibModel', label: 'Li3ibModel' },
            { value: 'Management', label: 'Management' },
            { value: 'Subscription', label: 'Subscription' },
            { value: 'Package', label: 'Package' },
          ]}
        />
      </Form.Item>

      <Form.Item label='Select Company' name='company_ids'>
        <Select
          showSearch
          allowClear
          placeholder='Select Company'
          loading={companyLoading}
          options={getSelectOptions(companyOptions)}
        />
      </Form.Item>

      <Form.Item label='Select Facility' name='facility_ids'>
        <Select
          showSearch
          allowClear
          placeholder='Select Facility'
          loading={facilityLoading}
          options={getSelectOptions(facilityOptions)}
          filterOption={(input, option) =>
            String(option?.label).toLowerCase().includes(input.toLowerCase())
          }
        />
      </Form.Item>

      <Form.Item label='Select Field' name='field_id'>
        <Select
          showSearch
          allowClear
          placeholder='Select Field'
          loading={fieldLoading}
          disabled={!facilityId || facilityLoading}
          options={getSelectOptions(fieldOptions)}
        />
      </Form.Item>

      <Form.Item label='Select Sport' name='sports_id'>
        <Select
          showSearch
          allowClear
          placeholder='Select Sport'
          loading={sportLoading}
          disabled={!fieldId || fieldLoading}
          options={getSelectOptions(sportOptions)}
        />
      </Form.Item>

      <Form.Item label='Date' name='date'>
        <RangePicker onChange={handleDate as any} />
      </Form.Item>
    </FormSectionsSeparator>
  );
};
